import { Check, ChevronRight } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router';
import { PageMotion } from 'src/components/layout/page-motion.tsx';
import { COMPANY_SIZES, COMPANY_TYPES, ROLES } from 'src/data/context-options.ts';
import { ADVANCE_DELAY_MS } from 'src/lib/constants.ts';
import { useCheck } from 'src/state/check-context.ts';

import { BackButton } from '../components/ui/back-button.tsx';
import { Eyebrow } from '../components/ui/eyebrow.tsx';

export function ContextPage() {
  const navigate = useNavigate();
  const { context, setContext, contextStep, setContextStep, answers } = useCheck();

  if (Object.keys(answers).length === 0) {
    return <Navigate to="/" replace />;
  }

  const config = [
    {
      field: 'size' as const,
      title: 'Wie viele Mitarbeitende sind in Ihrem Unternehmen beschäftigt?',
      options: COMPANY_SIZES,
      current: context.size
    },
    {
      field: 'type' as const,
      title: 'Sie arbeiten bei einer/einem …',
      options: COMPANY_TYPES,
      current: context.type
    },
    {
      field: 'role' as const,
      title: 'Welche Rolle nehmen Sie im Unternehmen ein?',
      options: ROLES,
      current: context.role
    }
  ][contextStep];

  function onPick(field: 'size' | 'type' | 'role', value: string) {
    setContext((c) => ({ ...c, [field]: value }));
    setTimeout(() => {
      if (contextStep < 2) setContextStep(contextStep + 1);
      else navigate('/lead');
    }, ADVANCE_DELAY_MS);
  }

  function onBack() {
    if (contextStep > 0) setContextStep(contextStep - 1);
    else navigate('/check');
  }

  return (
    <PageMotion>
      <div className="mx-auto max-w-3xl px-5 lg:px-10 py-12 lg:py-20">
        <Eyebrow>Kontext {contextStep + 1} von 3</Eyebrow>
        <h2
          className="mt-4 text-title sm:text-heading-sm leading-[1.25] tracking-tight text-zeb-tertiary"
          style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, letterSpacing: '-0.02em' }}>
          {config.title}
        </h2>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {config.options.map((opt) => {
            const selected = config.current === opt;
            return (
              <button
                key={opt}
                onClick={() => onPick(config.field, opt)}
                className={`text-left rounded-md border px-5 py-4 transition-all hover:-translate-y-px hover:shadow-zeb-xs active:scale-[0.99] motion-reduce:transform-none motion-reduce:transition-none ${
                  selected
                    ? 'border-zeb-primary bg-zeb-info-surface'
                    : 'border-zeb-muted bg-white hover:border-zeb-gray-300 hover:bg-zeb-surface'
                }`}>
                <div className="flex items-center justify-between gap-3">
                  <span
                    className={`text-body-sm ${
                      selected ? 'text-zeb-primary' : 'text-zeb-tertiary'
                    }`}>
                    {opt}
                  </span>
                  {selected ? (
                    <Check className="h-4 w-4 text-zeb-primary shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-zeb-gray-400 shrink-0" />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-10">
          <BackButton onClick={onBack} />
        </div>
      </div>
    </PageMotion>
  );
}
